var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useEffect, useRef, useState } from "react";
import { Flex } from "../Flex/Flex";
import { View } from "../View/View";
import { v4 as uuidv4 } from "uuid";
import { RawTooltip } from "../Tooltip";
import { useSubSection } from "../SubSection";
import { TMS_THEME } from "../theming/theming";
import { useAgendaStyles } from "./Agenda.styles";
import { useUtilityStyles, UtilityStyle } from "../hooks/useUtilityStyles";
import { classNames } from "../utils/classNames";
import { Prompt } from "../Prompt";
var AgendaRaw = function (props) {
    var _a;
    return (React.createElement(Flex, { direction: "column", role: "list" }, props === null || props === void 0 ? void 0 :
        props.children, (_a = props === null || props === void 0 ? void 0 : props.data) === null || _a === void 0 ? void 0 :
        _a.map(function (row, i) {
            return React.createElement(AgendaRow, __assign({}, row, { key: i }));
        })));
};
var AgendaRow = function (row) {
    var _a = useSubSection(!!(row === null || row === void 0 ? void 0 : row.children)), show = _a.show, toggleOpen = _a.toggleOpen;
    var componentId = uuidv4();
    var classes = useAgendaStyles(row);
    return (React.createElement(View, { role: "listitem" },
        (row === null || row === void 0 ? void 0 : row.children) === undefined ? (React.createElement(PrimaryContent, __assign({}, row, { extra: undefined }))) : (React.createElement("div", { onClick: function () { return toggleOpen(); }, role: "button", onKeyPress: function () { return toggleOpen(); }, tabIndex: 0, "aria-expanded": show === "open", "aria-controls": componentId },
            React.createElement(PrimaryContent, __assign({}, row, { extra: show })))),
        (show === "opening" || show === "open") && (React.createElement("div", { id: componentId, className: classes.subSectionContainer }, row === null || row === void 0 ? void 0 : row.children))));
};
var AgendaAlert = function (alertRow) {
    var getBgType = function (type) {
        var typeMap = {
            success: "success",
            info: "info",
            warning: "warning",
            error: "danger",
        };
        return typeMap[type];
    };
    return (React.createElement("div", { className: "bg-".concat(getBgType(alertRow.type)), style: { "--bs-bg-opacity": 0.1 } },
        React.createElement("div", { style: { padding: "5px 0px 0px 10px" } },
            React.createElement(Prompt, { type: alertRow.type, message: alertRow.alertMessage })),
        alertRow.children));
};
var PrimaryContent = function (row) {
    var wrapperRef = useRef(null);
    var _a = useState(false), ellipseContent = _a[0], setEllipseContent = _a[1];
    var overflowTooltipId = uuidv4();
    useEffect(function () {
        var currentElement = wrapperRef.current && wrapperRef.current.offsetHeight;
        if (!ellipseContent && currentElement !== null && currentElement > 21) {
            setEllipseContent(true);
        }
    }, [wrapperRef.current]);
    var classes = useAgendaStyles(row);
    var utilityClasses = useUtilityStyles([
        { bold: [UtilityStyle.FontBold] },
        { flex1: [UtilityStyle.FlexOne] },
    ]);
    return (React.createElement("div", { style: (row === null || row === void 0 ? void 0 : row.extra) === "open"
            ? { backgroundColor: TMS_THEME.palette.gray.aliceBlue.color }
            : {} },
        React.createElement(View, { paddingTop: "small", paddingBottom: "medium", hover: (row === null || row === void 0 ? void 0 : row.extra) !== undefined ? "aliceBlue" : undefined },
            React.createElement(Flex, null,
                (row === null || row === void 0 ? void 0 : row.indicator) && (React.createElement("div", { className: classes.indicatorContainer },
                    React.createElement(Flex, { justifyContent: "end" },
                        React.createElement("div", { className: classes.indicator })))),
                React.createElement("div", { className: classes.datetimeContainer },
                    React.createElement(Flex, { direction: "column" },
                        React.createElement("div", null, row.time),
                        row.secondaryTime && (React.createElement("div", { className: "".concat(classes.elementHeight, " ").concat(classes.metaInformation) }, row.secondaryTime)))),
                React.createElement("div", { "data-tip": !!ellipseContent, "data-for": overflowTooltipId, className: utilityClasses.flex1, id: "anchor_".concat(overflowTooltipId) },
                    React.createElement("div", { ref: wrapperRef, className: classNames(ellipseContent ? "ellipse-content line-clamp-one" : undefined, utilityClasses.bold) }, row.description),
                    row.metaInformation && (React.createElement("div", { className: "".concat(classes.elementHeight, " ").concat(classes.metaInformation) }, row.metaInformation)),
                    ellipseContent && (React.createElement(RawTooltip, { anchorId: "anchor_".concat(overflowTooltipId), content: row.description }))),
                row.extra !== undefined && (React.createElement("div", { className: classes.arrowContainer },
                    React.createElement("span", { className: "sub-section-header-indicator glyphicon glyphicon-chevron-right ".concat(row.extra) })))))));
};
export var Agenda = Object.assign(AgendaRaw, {
    Row: AgendaRow,
    Alert: AgendaAlert,
});
